import type { MatchEvent } from "@arcade-hockey/shared";
import { resetPlayersToSpawn } from "../state/initial.js";
import { tuning } from "../state/tuning.js";
import type { MatchState, PlayerState } from "../state/types.js";

// Returns the y-coordinate at the line crossing, or NaN if no crossing occurred.
// Using NaN as the "no crossing" sentinel keeps this allocation-free in the hot
// path; callers test with `Number.isNaN`.
function lineCrossY(
  prevX: number,
  prevY: number,
  nextX: number,
  nextY: number,
  lineX: number,
  direction: 1 | -1,
): number {
  // direction = +1 means we're scoring when crossing from x < lineX to x > lineX.
  const wasBefore = direction === 1 ? prevX < lineX : prevX > lineX;
  const isAfter = direction === 1 ? nextX >= lineX : nextX <= lineX;
  if (!wasBefore || !isAfter) return Number.NaN;
  const dx = nextX - prevX;
  if (dx === 0) return Number.NaN;
  const t = (lineX - prevX) / dx;
  return prevY + (nextY - prevY) * t;
}

export function tickGoalDetection(state: MatchState, prevPuckX: number, prevPuckY: number): void {
  const halfL = tuning.rink.length / 2;
  const halfMouth = tuning.rink.goalMouthHalfWidth;
  const awayGoalLineX = halfL - tuning.rink.goalLineOffset;
  const homeGoalLineX = -halfL + tuning.rink.goalLineOffset;

  if (state.clock.phase === "GoalCelebration") {
    // Wait for celebration to end.
    if (state.clock.remainingTicks <= 0) {
      // Reset to faceoff at center: puck + every skater back to their
      // spawn position, action/stamina cleared. Otherwise players stay
      // wherever the previous play ended and the new faceoff plays from
      // a busted shape.
      state.puck.pos = { x: 0, y: 0 };
      state.puck.vel = { x: 0, y: 0 };
      state.puck.mode = "Loose";
      state.puck.ownerId = null;
      state.puck.lastTouchedBy = null;
      state.puck.noRecaptureUntilTick = state.tick + tuning.clock.faceoffPauseTicks;
      resetPlayersToSpawn(state.players);
      state.clock.phase = "Faceoff";
      state.clock.remainingTicks = tuning.clock.faceoffPauseTicks;
      const ev: MatchEvent = { type: "Faceoff", tick: state.tick, spot: "Center" };
      state.events.push(ev);
    }
    return;
  }

  if (state.clock.phase === "Faceoff") {
    return;
  }

  // Slices 8 / 9 — Overtime and Shootout are also live-play phases for
  // goal detection. Sudden-death OT goals end the game immediately; a
  // shootout goal is recorded by the shootout machine (it watches the
  // GoalScored event), so we don't end the game here.
  if (
    state.clock.phase !== "Play" &&
    state.clock.phase !== "Overtime" &&
    state.clock.phase !== "Shootout"
  ) {
    return;
  }
  const isOvertimeGoal = state.clock.phase === "Overtime";

  // Slice 7 — Controlled-mode goal: release-on-cross.
  //
  // The puck is hard-pinned to the carrier's stick anchor at 0.9 m, so
  // rotating `stickDir` 180° in one tick teleports the puck through a
  // 1.8 m arc — that's the "spin-on-stick own-goal" artifact we never
  // want to count. But a legitimate wraparound (carrier driving the
  // net with the puck on their stick) should score.
  //
  // The distinguishing signal is the CARRIER'S BODY VELOCITY toward
  // the goal line. Spin-on-stick has zero body velocity; a wraparound
  // or stuff-in has > 1 m/s. We require closing speed > 1 m/s for a
  // Controlled-mode goal, and release the puck off the stick on the
  // crossing tick so the post-score state is consistent (Loose puck in
  // the net, not Controlled).
  let controlledOwnerToRelease: PlayerState | null = null;
  if (state.puck.mode === "Controlled") {
    if (state.puck.ownerId === null) return;
    const owner = state.playersById.get(state.puck.ownerId);
    if (owner === undefined) return;
    // Closing speed: positive vel.x for the away goal at +x; negative
    // vel.x for the home goal at -x. Use the puck's side of centre ice
    // to pick which.
    const closingSpeed = state.puck.pos.x > 0 ? owner.vel.x : -owner.vel.x;
    if (closingSpeed < tuning.goal.controlledMinClosingSpeed) return;
    // Mark the owner; release the puck only after we've confirmed a
    // crossing below. If neither line crosses this tick, we leave the
    // puck Controlled and play continues.
    controlledOwnerToRelease = owner;
  }

  const puckX = state.puck.pos.x;
  const puckY = state.puck.pos.y;

  // Check away goal (home scores).
  const awayCrossY = lineCrossY(prevPuckX, prevPuckY, puckX, puckY, awayGoalLineX, 1);
  if (!Number.isNaN(awayCrossY) && Math.abs(awayCrossY) <= halfMouth) {
    if (controlledOwnerToRelease !== null) releasePuckOffStick(state, controlledOwnerToRelease);
    state.score.home += 1;
    const ev: MatchEvent = {
      type: "GoalScored",
      tick: state.tick,
      teamId: "home",
      scorer: state.puck.lastTouchedBy ?? undefined,
    };
    state.events.push(ev);
    if (isOvertimeGoal) {
      // Sudden-death — game ends immediately. No celebration phase
      // (in real NHL the celebration is on-ice; gameplay has stopped).
      state.clock.phase = "GameOver";
      state.events.push({ type: "MatchEnded", tick: state.tick, winner: "home" });
    } else if (state.clock.phase === "Shootout") {
      // Shootout goal — `tickShootout` reads the GoalScored event and
      // advances its sub-phase machine. We do NOT enter GoalCelebration
      // and we do NOT bump the visible scoreboard; the shootout score
      // sits on its own state field and applies only when the shootout
      // ends.
      state.score.home -= 1;
    } else {
      state.clock.phase = "GoalCelebration";
      state.clock.remainingTicks = tuning.clock.goalCelebrationTicks;
    }
    return;
  }

  // Check home goal (away scores).
  const homeCrossY = lineCrossY(prevPuckX, prevPuckY, puckX, puckY, homeGoalLineX, -1);
  if (!Number.isNaN(homeCrossY) && Math.abs(homeCrossY) <= halfMouth) {
    if (controlledOwnerToRelease !== null) releasePuckOffStick(state, controlledOwnerToRelease);
    state.score.away += 1;
    const ev: MatchEvent = {
      type: "GoalScored",
      tick: state.tick,
      teamId: "away",
      scorer: state.puck.lastTouchedBy ?? undefined,
    };
    state.events.push(ev);
    if (isOvertimeGoal) {
      state.clock.phase = "GameOver";
      state.events.push({ type: "MatchEnded", tick: state.tick, winner: "away" });
    } else if (state.clock.phase === "Shootout") {
      // Shootout goal — handled by `tickShootout`. See the matching
      // branch in the home-goal block for why we roll back the visible
      // scoreboard.
      state.score.away -= 1;
    } else {
      state.clock.phase = "GoalCelebration";
      state.clock.remainingTicks = tuning.clock.goalCelebrationTicks;
    }
  }
}

/**
 * Slice 7 — Drop the puck off the carrier's stick on a wraparound goal,
 * so the post-score puck state is Loose (not Controlled with the carrier
 * deep behind the net). The puck inherits the owner's velocity, mirroring
 * the natural "deflection off the stick into the net" feel.
 */
function releasePuckOffStick(state: MatchState, owner: PlayerState): void {
  state.puck.mode = "Loose";
  state.puck.ownerId = null;
  state.puck.vel.x = owner.vel.x;
  state.puck.vel.y = owner.vel.y;
  state.puck.lastTouchedBy = owner.id;
  owner.hasPuck = false;
  owner.possessionQuality = 0;
}
